import { useQuery } from "@tanstack/react-query";
import { useEmployeePerformance } from "@/hooks/use-hr-queries";
import { hrEmployeeApi } from "@/lib/api/hr";
import { getHrEmployeeSession } from "@/lib/auth/storage";
import { EmptyState, Page, Panel, QueryGate, Stat } from "@/components/shell/page";

export default function EmployeePerformancePage() {
  const { data, isLoading, error } = useEmployeePerformance();
  const session = getHrEmployeeSession();
  const awards = useQuery({
    queryKey: ["hr", "employee", "awards", session?.employee.id],
    queryFn: () => hrEmployeeApi.awards(),
    enabled: !!session,
  });

  return (
    <QueryGate loading={isLoading} error={error || !data ? "Could not load points." : null}>
      {data && (
        <Page title="Performance" description="Points are the sum of each transaction, not a single stored number.">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Total points" value={data.total} />
            <Stat label="This month" value={data.month} />
            <Stat label="From awards" value={data.award_points} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Point history">
              {(data.history ?? []).length === 0 && (
                <EmptyState title="No points yet" body="Completing tasks and receiving awards adds lines here." />
              )}
              <ul className="divide-y">
                {(data.history ?? []).map((row: any) => (
                  <li key={row.id} className="flex items-center justify-between gap-3 py-3 text-sm first:pt-0 last:pb-0">
                    <span>{row.description || row.reason_code}</span>
                    <span className={row.amount > 0 ? "font-medium tabular-nums text-emerald-800" : "font-medium tabular-nums text-red-700"}>
                      {row.amount > 0 ? `+${row.amount}` : row.amount}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
            <Panel title="Awards">
              {(awards.data ?? []).length === 0 && (
                <EmptyState title="No awards yet" body="Awards from your employer will appear here." />
              )}
              <ul className="space-y-4">
                {(awards.data ?? []).map((award: any) => (
                  <li key={award.id}>
                    <p className="font-medium">{award.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {award.awarded_on} · {award.awarded_by} · {award.points} pts
                    </p>
                    {award.description && <p className="mt-1 text-sm">{award.description}</p>}
                  </li>
                ))}
              </ul>
            </Panel>
          </div>
        </Page>
      )}
    </QueryGate>
  );
}
